import { ItemType } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';

type LostEligibilityItem = {
  title?: string | null;
  subTitle?: string | null;
  options?: React.ReactNode[];
  disclaimer1?: React.ReactNode;
  disclaimer2?: React.ReactNode;
};

const ContentItem = ({ item }: { item: LostEligibilityItem }) => (
  <View key={item.title} style={styles.defaultStyle}>
    <BodyBaseBoldText numberOfLines={0}>{item.title}</BodyBaseBoldText>
    <BodyBaseText numberOfLines={0}>{item.subTitle}</BodyBaseText>
    <View style={styles.listContainer}>
      <ContentList
        itemType={ItemType.Bullet}
        contentList={item.options ?? []}
      />
    </View>
    {item.disclaimer1 ? (
      <BodyBaseText numberOfLines={0}>{item.disclaimer1}</BodyBaseText>
    ) : null}
    {item.disclaimer2 ? (
      <BodyBaseText style={styles.defaultStyle} numberOfLines={0}>
        {item.disclaimer2}
      </BodyBaseText>
    ) : null}
  </View>
);

type LostEligibilityScreenProps = {
  data?: {
    vlocLostEligibilityBody?: {
      title?: string | null;
      subTitle?: string | null;
      items?: LostEligibilityItem[];
    };
  };
};

const LostEligibilityScreen = ({ data }: LostEligibilityScreenProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');
  const { bottom } = useSafeAreaInsets();

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goToManageAccounts = () => {
    router.navigate('/varo-line-of-credit/manage-account');
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/lost-eligibility',
  });

  const content = data?.vlocLostEligibilityBody ?? null;

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScrollView>
        <Stack.Screen
          options={{
            title: translatedText('vlocGeneral.lineOfCreditTitle'),
            headerBackVisible: true,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                trailingAccessories={
                  <IconButton
                    iconKey="SETTINGS"
                    iconButtonAccessibilityLabel="SETTINGS"
                    iconOnPress={goToManageAccounts}
                    analyticsId="lost_eligibility_manageAccountClicked"
                  />
                }
                primaryOnPress={goHome}
                {...props}
              />
            ),
          }}
        />
        <View style={styles.content}>
          {content?.title ? (
            <TitleLgText style={styles.title} numberOfLines={0}>
              {content.title}
            </TitleLgText>
          ) : null}
          {content?.subTitle ? (
            <BodyBaseText style={styles.body} numberOfLines={0}>
              {content.subTitle}
            </BodyBaseText>
          ) : null}
          <View>
            {content?.items?.map((item) => (
              <ContentItem key={item.title} item={item} />
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    marginVertical: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.m,
  },
  content: {
    flex: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: theme.spacing.m,
  },
  defaultStyle: {
    marginTop: theme.spacing.s,
  },
  listContainer: {
    marginVertical: theme.spacing.m,
  },
  title: {
    marginBottom: theme.spacing.s,
  },
});

export default LostEligibilityScreen;
